import React, { useCallback, useEffect, useState } from 'react';
import { Group } from '@mantine/core';
import { useSelector } from 'react-redux';
import { IAppState } from '../../../../lib/types/AppState/index';
import { IOrgJob, ISourceMatch, ISubmitMatchesResponse } from '../PropagateMatchesTypes';
import { clearSelectedMatches, createSourceJob, processSurveyMatch } from '../helpers';
import { getMatches } from '../PropagateMatchesAPIs';
import TabsList from './TabsList';

interface Props {
  orgJobs: IOrgJob[];
}

export default function TabsContainer({ orgJobs }: Props) {
  const activeSurveyYear = useSelector((state: IAppState) => state.session.benchmarkYear);
  const masterAccountID = useSelector((state: IAppState) => state.session.organization.masterAccountId);
  const [activeTab, setActiveTab] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(true);
  const [surveyJobMatches, setSurveyJobMatches] = useState<ISourceMatch[]>([]);
  const [toggledTargetMatches, setToggledTargetMatch] = useState<string[]>([]);



  useEffect(() => {
    if (orgJobs.length > 0) {
      setActiveTab(`${orgJobs[0].orgJobId}`);
    }
  }, [orgJobs]);

  useEffect(() => {
    if (activeTab) {
      setLoading(true);
      fetchSurveyJobMatchData();
    }

    return () => {
      resetState();
    };
  }, [activeTab]);

  const fetchSurveyJobMatchData = useCallback(
    async ({
    resetSelectedMatches = false,
    surveyJobMatch,
    matchesMade
  }: { resetSelectedMatches?: boolean, surveyJobMatch?: ISourceMatch, matchesMade?: ISubmitMatchesResponse } = {}) => {
      const selectedJob = orgJobs.find(orgJob => orgJob.orgJobId === parseInt(activeTab));
      if (!selectedJob) return;

      const sourceJob = createSourceJob(selectedJob, surveyJobMatch);
      const [orgJobData] = await getMatches({
        masterAccountID,
        sourceJobs: [sourceJob],
        activeSurveyYear,
      });

      const updatedSurveyMatches = orgJobData.surveyMatches.map((match: ISourceMatch) =>
        processSurveyMatch(match, surveyJobMatches)
      );

      if (resetSelectedMatches && matchesMade) {
        setSurveyJobMatches(clearSelectedMatches(updatedSurveyMatches, matchesMade));
      } else if (surveyJobMatch) {
        const replacedMatches = surveyJobMatches.map(match =>
          match.sourceMatchId === surveyJobMatch.sourceMatchId ? updatedSurveyMatches[0] : match
        );
        setSurveyJobMatches(replacedMatches);
      } else {
        setSurveyJobMatches(updatedSurveyMatches);
      }

      setLoading(false);
    },
    [activeTab, activeSurveyYear, masterAccountID, orgJobs, surveyJobMatches]
  );

  const resetState = useCallback(() => {
    setSurveyJobMatches([]);
    setToggledTargetMatch([]);
  }, []);


  const updateToggledMatches = useCallback((id: string) => {
    setToggledTargetMatch(prev =>
      prev.includes(id) ? prev.filter(match => match !== id) : [...prev, id]
    );
  }, []);

  return (
    <Group
      grow
      style={{
        borderTop: '1px solid var(--mantine-color-ghost-6)',
        flexWrap: 'nowrap',
        width: '100%',
      }}
    >
      <TabsList
        activeTab={activeTab}
        fetchSurveyJobMatchData={fetchSurveyJobMatchData}
        isLoading={isLoading}
        orgJobs={orgJobs}
        setActiveTab={setActiveTab}
        setSurveyJobMatches={setSurveyJobMatches}
        surveyJobMatches={surveyJobMatches}
        toggledTargetMatches={toggledTargetMatches}
        updateToggledMatches={updateToggledMatches}
      />
    </Group>
  );
}
